import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Row, Col, Divider, notification, Space } from "antd";
import CustomButton from "../../../common/Buttons/CustomButton";
import styles from "./Payment.module.scss";
import { backArrowIcon, wandaIcon } from "../../../assets/images";
import {
  ITransactionDetails,
  PaymentTransactionTypeIds,
  defaultTransactionDetails,
} from "../../../types/PaymentTransactionTypes";
import moment from "moment";
import RefundedServiceInfo from "../../Shared/RefundedServiceInfo";
import { ITripDetails } from "../../ServiceUsers/Interfaces/ITripDetails";
import { getTripByRequestAssignmentId } from "../../../services/RegistrationService";
import { defaultTripDetails } from "../../ServiceUsers/ServiceUsersMobile/defaultTripDetails";
import TripDetailsModal from "../../Shared/Modals/TripDetailsModal";
import { useBillingHistoryCtx } from "../../../context/BillingHistoryContext";
import GoogleLoader from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import getTripIcon from "../../../helpers/getTripIcon";
import { formatUTCtoLocalDate } from "../../../helpers/dateFormatters";
import { WandaTripStatus } from "../../../types/ApplicationTypes";

interface IParams {
  paymentTransactionId: string;
}

const BillingHistoryMobileTransaction = () => {
  const [tripLeg, setTripLeg] = useState<ITripDetails[]>([defaultTripDetails]);
  const [isTripIdModalVisible, setIsTripIdModalVisible] = useState<boolean>(
    false
  );
  const [transaction, setTransaction] = useState<ITransactionDetails>(
    defaultTransactionDetails
  );

  const history = useHistory();
  const params = useParams<IParams>();

  const { billingHistoryCtx } = useBillingHistoryCtx();
  const [billingHistory] = billingHistoryCtx;

  useEffect(() => {
    const filteredTransaction = billingHistory.find(
      (transaction: ITransactionDetails) =>
        transaction.paymentTransactionId === Number(params.paymentTransactionId)
    );

    setTransaction(filteredTransaction);
  }, [params.paymentTransactionId]);

  window.addEventListener("load", () =>
    history.push(`/DashBoard/BillingHistory`)
  );

  const handleTripIdAndRefundedTripId = async () => {
    try {
      const apiResponse = await getTripByRequestAssignmentId({
        requestAssignmentId: transaction.requestAssignmentId,
        personId: transaction.customerPersonId,
        favoriteId: 0,
        tripDate: moment.utc(transaction.createdOn).format(),
      });
      setTripLeg(
        apiResponse.personTrips.filter(
          (trip: ITripDetails) => Number(trip.tripId) === transaction.tripId
        )
      );
      setIsTripIdModalVisible(true);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to Fetch Trips",
      });
    }
  };

  const renderTransaction = (transactionType: string) => {
    switch (transactionType) {
      case "Service":
        return (
          <>
            <Col span={12}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Service User
                </Col>
                <Col span={24} className={styles.description}>
                  {transaction?.serviceUser}
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Service Type
                </Col>
                <Col span={24} className={styles.description}>
                  <Space>
                    {getTripIcon(transaction?.serviceType)}
                    {transaction?.serviceType}
                  </Space>
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Trip ID
                </Col>
                <Col span={24} className={styles.hyperlink}>
                  <CustomButton
                    text={transaction?.tripId.toString()}
                    type="link"
                    handlerFunction={handleTripIdAndRefundedTripId}
                    className="hyperLinkBtn"
                  />
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Booking ID
                </Col>
                <Col span={24} className={styles.hyperlink}>
                  <CustomButton
                    text={transaction?.bookingId.toString()}
                    type="link"
                    handlerFunction={handleBookingId}
                    className="hyperLinkBtn"
                  />
                </Col>
              </Row>
            </Col>
          </>
        );

      case "Subscription":
        return (
          <>
            <Col span={24}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Subscription Type
                </Col>
                <Col span={24} className={styles.description}>
                  <img
                    src={wandaIcon}
                    className={styles.icon}
                    alt="wandaPlusSubscription"
                  />
                  Wanda Plus
                </Col>
              </Row>
            </Col>
          </>
        );

      case "Refund":
        return (
          <>
            {transaction?.details && (
              <Col span={24}>
                <Row>
                  <Col span={24} className={styles.subTitle}>
                    Refund Details
                  </Col>
                  <Col span={24} className={styles.description}>
                    {transaction?.details}
                  </Col>
                </Row>
              </Col>
            )}
            <Col span={12}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Refunded Trip ID
                </Col>
                <Col span={24} className={styles.hyperlink}>
                  <CustomButton
                    text={transaction?.tripId?.toString()}
                    type="link"
                    handlerFunction={handleTripIdAndRefundedTripId}
                    className="hyperLinkBtn"
                  />
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Refunded Transaction ID
                </Col>
                <Col span={24} className={styles.description}>
                  {transaction?.refundedTransactionId}
                </Col>
              </Row>
            </Col>
          </>
        );
    }
  };

  const handleBookingId = () =>
    history.push(
      `/DashBoard/TransportationServiceSetupSummary/${transaction.customerPersonId}/${transaction.requestAssignmentId}/${transaction.bookingId}/${WandaTripStatus.Completed}/${transaction.tripDate}`
    );

  const handleBack = () => history.push("/DashBoard/BillingHistory");

  return !transaction?.paymentTransactionId ? (
    <GoogleLoader />
  ) : (
    <>
      <Row className={styles.mobileTransactionContainer}>
        <Col span={2} />

        <Col span={20}>
          <Row>
            <Col lg={{ offset: 1, span: 23 }}>
              <CustomButton
                text="Back"
                handlerFunction={handleBack}
                type="link"
                icon={backArrowIcon}
                className="backBtn"
              />
            </Col>
          </Row>

          <Row className={styles.title}>Transaction Details</Row>

          {transaction?.paymentTransactionTypeId ===
            PaymentTransactionTypeIds.Service &&
            transaction?.refundedTransactionId && (
              <RefundedServiceInfo
                refundedAmount={transaction?.amount}
                refundedDate={transaction?.createdOn}
                refundedTransactionId={transaction?.refundedTransactionId}
              />
            )}

          <Row>
            <Col span={12}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Transaction Date
                </Col>
                <Col span={24} className={styles.description}>
                  {formatUTCtoLocalDate(transaction?.createdOn, "MM/DD/YYYY")}
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Amount
                </Col>
                <Col span={24} className={styles.description}>
                  {transaction.paymentTransactionTypeId ===
                    PaymentTransactionTypeIds.Refund && "+"}
                  ${transaction?.amount?.toFixed(2)}
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Transaction Type
                </Col>
                <Col span={24} className={styles.description}>
                  {transaction?.paymentTransactionTypeName}
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Transaction ID
                </Col>
                <Col span={24} className={styles.description}>
                  {transaction?.paymentTransactionId}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row>
                <Col span={24} className={styles.subTitle}>
                  Payment Type
                </Col>
                <Col span={24} className={styles.description}>
                  {transaction?.paymentInstrumentName}
                </Col>
              </Row>
            </Col>
          </Row>

          <Divider className={styles.divider} dashed />

          <Row>
            {renderTransaction(transaction?.paymentTransactionTypeName)}
          </Row>
        </Col>

        <Col span={2} />
      </Row>

      <TripDetailsModal
        visible={isTripIdModalVisible}
        setVisibility={setIsTripIdModalVisible}
        trip={tripLeg}
      />
    </>
  );
};

export default BillingHistoryMobileTransaction;
